import React, { useCallback, useEffect, useState } from 'react';
import { ListChecks, CheckCircle2, Circle, ChevronRight, Plus, Minus, BarChart3 } from 'lucide-react';
import type { WeeklyPlanResponse, WeeklyPlanUpdate, WeeklyPulseFigures } from '@crewcast/shared';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import KudosModal from './KudosModal';
import Skeleton from './Skeleton';

/** "Oct 26" from a YYYY-MM-DD week start (a calendar date, so no timezone shift). */
function formatDay(ymd: string, offsetDays = 0): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + offsetDays))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "Andrew (2), Sam": the count only when someone has mentioned it more than once. */
function mentionsByline(by: WeeklyPlanResponse['items']['shiftMentions']['by']): string {
  return by.map((b) => (b.count > 1 ? `${b.name} (${b.count})` : b.name)).join(', ');
}

function ProgressBar({ value, target }: { value: number; target: number }) {
  return (
    <div className="game-plan-bar">
      <div className="game-plan-bar-fill" style={{ width: `${Math.min(value / target, 1) * 100}%` }} />
    </div>
  );
}

function ItemRow({ done, title, children, onClick, chevron }: {
  done: boolean;
  title: string;
  children?: React.ReactNode;
  onClick?: () => void;
  chevron?: boolean;
}) {
  const content = (
    <>
      {done
        ? <CheckCircle2 size={22} color="#00bf62" className="game-plan-check" />
        : <Circle size={22} color="#d1d5db" className="game-plan-check" />}
      <div className="game-plan-item-body">
        <div className={`game-plan-item-title${done ? ' done' : ''}`}>{title}</div>
        {children}
      </div>
      {chevron && <ChevronRight size={18} color="#9ca3af" />}
    </>
  );
  if (!onClick) return <div className="game-plan-item">{content}</div>;
  return (
    <div
      className="game-plan-item clickable"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      {content}
    </div>
  );
}

const PULSE_ROWS: Array<{ key: keyof WeeklyPulseFigures; label: string }> = [
  { key: 'momentsLaunched', label: 'Moments launched' },
  { key: 'submissions', label: 'Submissions' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'postedToSocials', label: 'Posted to socials' },
  { key: 'activeContributors', label: 'Active contributors' },
];

function Delta({ current, previous }: { current: number; previous: number }) {
  const diff = current - previous;
  if (diff === 0) return <span className="pulse-delta same">same</span>;
  return <span className={`pulse-delta ${diff > 0 ? 'up' : 'down'}`}>{diff > 0 ? `▲ +${diff}` : `▼ ${diff}`}</span>;
}

interface Props {
  /** Bumped by the dashboard when something the plan counts changes (e.g. a trend launched). */
  refreshKey: number;
  /** "Launch 3 new Moments": scroll to Suggested Trends, or open Moments. */
  onLaunchMoments: (suggestedTrendsEnabled: boolean) => void;
  onReviewSubmissions: () => void;
}

/**
 * Weekly Game Plan + last week's Pulse, shared by every admin of the company.
 * Web counterpart of mobile's WeeklyGamePlan; same API.
 */
export default function WeeklyGamePlanWidget({ refreshKey, onLaunchMoments, onReviewSubmissions }: Props) {
  const { token } = useAuth();
  const [plan, setPlan] = useState<WeeklyPlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pulseOpen, setPulseOpen] = useState(false);
  const [kudosOpen, setKudosOpen] = useState(false);

  const fetchPlan = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${authConfig.apiUrl}/api/admin/weekly-plan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setPlan(await res.json());
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan, refreshKey]);

  // Other admins update the same plan; pick up their changes when the tab regains focus.
  useEffect(() => {
    window.addEventListener('focus', fetchPlan);
    return () => window.removeEventListener('focus', fetchPlan);
  }, [fetchPlan]);

  const update = async (body: WeeklyPlanUpdate) => {
    try {
      const res = await fetch(`${authConfig.apiUrl}/api/admin/weekly-plan`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      setPlan(await res.json());
    } catch {
      fetchPlan(); // drop the optimistic change
    }
  };

  // Optimistic counts only; the PATCH response brings the real byline.
  const changeShiftMentions = (delta: 1 | -1) => {
    setPlan((p) => p && {
      ...p,
      items: {
        ...p.items,
        shiftMentions: {
          ...p.items.shiftMentions,
          count: p.items.shiftMentions.count + delta,
          mine: p.items.shiftMentions.mine + delta,
        },
      },
    });
    update({ action: delta === 1 ? 'shiftMention' : 'shiftMentionUndo' });
  };

  const toggleKudos = () => {
    const top = plan?.items.topContributor;
    if (!top) return;
    setPlan((p) => p && { ...p, items: { ...p.items, topContributor: { ...top, done: !top.done } } });
    update({ action: 'kudos', done: !top.done });
  };

  const openPulse = () => {
    setPulseOpen(true);
    if (!plan?.items.reviewPulse.done) update({ action: 'pulseViewed' });
  };

  // First load only: skeletons in the shape of both cards, so the dashboard
  // doesn't snap open when the plan arrives. Refetches keep the current plan.
  if (loading) {
    return (
      <div className="dashboard-grid game-plan-grid" aria-busy="true" aria-label="Loading your Game Plan">
        <div className="dashboard-widget">
          <div className="widget-header">
            <Skeleton width={180} height={16} />
            <Skeleton width={72} height={22} radius={11} />
          </div>
          <Skeleton width={240} height={11} style={{ marginTop: -8, marginBottom: 10 }} />
          <Skeleton height={6} radius={3} />
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="game-plan-item">
              <Skeleton width={22} height={22} radius={11} style={{ flexShrink: 0 }} />
              <div className="game-plan-item-body">
                <Skeleton width={i % 2 ? '55%' : '70%'} height={13} />
                <Skeleton width="40%" height={10} style={{ marginTop: 6 }} />
              </div>
            </div>
          ))}
        </div>
        <div className="dashboard-widget">
          <div className="widget-header">
            <Skeleton width={160} height={16} />
            <Skeleton width={90} height={11} />
          </div>
          <Skeleton width="85%" height={12} />
          <Skeleton width={190} height={36} radius={8} style={{ marginTop: 16 }} />
        </div>
      </div>
    );
  }
  if (error || !plan) {
    return (
      <div className="dashboard-grid-single">
        <div className="dashboard-widget game-plan-error">
          Couldn't load your Game Plan.{' '}
          <button type="button" className="game-plan-link" onClick={fetchPlan}>Retry</button>
        </div>
      </div>
    );
  }

  const { items, pulse } = plan;

  return (
    <div className="dashboard-grid game-plan-grid fade-in">
      {/* Weekly Game Plan */}
      <div className="dashboard-widget">
        <div className="widget-header">
          <h3 className="widget-title">
            <span className="widget-title-icon"><ListChecks size={20} color="#00bf62" /></span>
            Weekly Game Plan
          </h3>
          <span className="game-plan-count">{plan.completed}/{plan.total} done</span>
        </div>
        <div className="game-plan-subtitle">Week of {formatDay(plan.weekStart)} · shared with your admins</div>
        <ProgressBar value={plan.completed} target={plan.total} />

        <ItemRow
          done={items.launchMoments.done}
          title={`Launch ${items.launchMoments.target} new Moments`}
          onClick={() => onLaunchMoments(plan.suggestedTrendsEnabled)}
          chevron
        >
          <div className="game-plan-meta">
            {Math.min(items.launchMoments.count, items.launchMoments.target)}/{items.launchMoments.target} Launched
          </div>
          <ProgressBar value={items.launchMoments.count} target={items.launchMoments.target} />
        </ItemRow>

        <ItemRow done={items.shiftMentions.done} title="Mention CrewCast on shift">
          <div className="game-plan-inline">
            <div className="game-plan-inline-grow">
              <div className="game-plan-meta">
                Mentioned on shift {Math.min(items.shiftMentions.count, items.shiftMentions.target)}/{items.shiftMentions.target}
              </div>
              <ProgressBar value={items.shiftMentions.count} target={items.shiftMentions.target} />
            </div>
            <button
              type="button"
              className="game-plan-step"
              onClick={() => changeShiftMentions(-1)}
              disabled={items.shiftMentions.mine === 0}
              aria-label="Undo your last shift mention"
              title={items.shiftMentions.mine === 0 ? 'Nothing of yours to undo' : 'Undo your last shift mention'}
            >
              <Minus size={16} />
            </button>
            <button
              type="button"
              className="game-plan-step"
              onClick={() => changeShiftMentions(1)}
              aria-label="Log a shift mention"
              title="Log a shift mention"
            >
              <Plus size={16} />
            </button>
          </div>
          {items.shiftMentions.by.length > 0 && (
            <div className="game-plan-byline">by {mentionsByline(items.shiftMentions.by)}</div>
          )}
        </ItemRow>

        <ItemRow
          done={items.pendingApprovals.done}
          title="Approve pending submissions"
          onClick={items.pendingApprovals.count > 0 ? onReviewSubmissions : undefined}
          chevron={items.pendingApprovals.count > 0}
        >
          <span className={`game-plan-badge ${items.pendingApprovals.color}`}>
            {items.pendingApprovals.count === 0 ? 'All caught up' : `${items.pendingApprovals.count} pending`}
          </span>
        </ItemRow>

        {items.topContributor && (
          <ItemRow done={items.topContributor.done} title="Recognize your top contributor" onClick={toggleKudos}>
            <div className="game-plan-meta">
              Give {items.topContributor.name} a shout out · {items.topContributor.approvals} approved{' '}
              {items.topContributor.approvals === 1 ? 'submission' : 'submissions'} last week
            </div>
            {items.topContributor.done && items.topContributor.doneBy && (
              <div className="game-plan-byline">Done by {items.topContributor.doneBy}</div>
            )}
            <button
              type="button"
              className="game-plan-kudos-btn"
              onClick={(e) => { e.stopPropagation(); setKudosOpen(true); }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              Send kudos
            </button>
          </ItemRow>
        )}

        {kudosOpen && items.topContributor && (
          <KudosModal
            employee={{ userId: items.topContributor.userId, name: items.topContributor.name }}
            source="game-plan"
            onClose={() => setKudosOpen(false)}
            onSent={fetchPlan} // the item ticks itself for the top contributor
          />
        )}

        <ItemRow done={items.reviewPulse.done} title="Review last week's results" onClick={openPulse} chevron>
          {items.reviewPulse.doneBy && <div className="game-plan-byline">Viewed by {items.reviewPulse.doneBy}</div>}
        </ItemRow>
      </div>

      {/* Weekly Pulse */}
      <div className="dashboard-widget">
        <div className="widget-header">
          <h3 className="widget-title">
            <span className="widget-title-icon"><BarChart3 size={20} color="#00bf62" /></span>
            Last Week's Pulse
          </h3>
          <span className="game-plan-subtitle">{formatDay(pulse.weekStart)}–{formatDay(pulse.weekStart, 6)}</span>
        </div>
        {!pulseOpen ? (
          <div className="pulse-closed">
            <p>See how last week went, compared with the week before.</p>
            <button type="button" className="pulse-open-btn" onClick={openPulse}>See last week's results</button>
          </div>
        ) : (
          <div className="pulse-list">
            {PULSE_ROWS.map(({ key, label }) => (
              <div key={key} className="pulse-row">
                <span className="pulse-label">{label}</span>
                <span className="pulse-value">{pulse.current[key]}</span>
                <Delta current={pulse.current[key]} previous={pulse.previous[key]} />
              </div>
            ))}
            <div className="pulse-row">
              <span className="pulse-label">Game Plan completion</span>
              <span className="pulse-value">
                {pulse.completion ? `${pulse.completion.completed}/${pulse.completion.total}` : '—'}
              </span>
              {pulse.completion && pulse.previousCompletion
                ? <Delta current={pulse.completion.completed} previous={pulse.previousCompletion.completed} />
                : <span className="pulse-delta" />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
